// ============================================================================
// ENGAGEMENT MASTER SCREEN
// ============================================================================
// Master CRUD screen for Engagements via prima-api.
//
// Follows the ClientMasterScreen pattern with:
//  - FK lookups: clients, engagementTypes, managers, partners
//  - managerPartnerMap for auto-filling partner from manager
//  - Server-side pagination, search, filter
//  - Column visibility with ColumnSelector
//  - Minimize-to-bar support

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutScopedEmployeeSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import {
  useEngagements,
  useCreateEngagement,
  useUpdateEngagement,
  useDeleteEngagement,
} from 'hooks/api/useEngagement';
import { useClients } from 'hooks/api/useClient';
import { useEngagementTypes } from 'hooks/api/useEngagementTypes';
import { useEmployees } from 'hooks/api/useEmployee';
import type {
  Engagement,
  EngagementSearchRequest,
  CreateEngagementRequest,
  UpdateEngagementRequest,
} from 'api/types';
import EngagementCloseModal from 'components/modals/modules/engagement/EngagementCloseModal';
import EngagementReopenModal from 'components/modals/modules/engagement/EngagementReopenModal';
import EngagementClosureHistoryModal from 'components/modals/modules/engagement/EngagementClosureHistoryModal';
import EngagementFormModal, {
  type EngagementFormData,
} from 'components/modals/modules/engagement/EngagementFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const STATUS_BADGE_MAP: Record<string, { className: string; label: string }> = {
  OPEN:        { className: 'badge-active',   label: 'Open' },
  IN_PROGRESS: { className: 'badge-info',     label: 'In Progress' },
  ON_HOLD:     { className: 'badge-pending',  label: 'On Hold' },
  COMPLETED:   { className: 'badge-active',   label: 'Completed' },
  CLOSED:      { className: 'badge-inactive', label: 'Closed' },
  ACTIVE:      { className: 'badge-active',   label: 'Active' },
  INACTIVE:    { className: 'badge-inactive', label: 'Inactive' },
  DRAFT:       { className: 'badge-pending',  label: 'Draft' },
};

const currentYear = new Date().getFullYear();

/** Year filter options: current year +/- 5 */
const YEAR_FILTER_OPTIONS: { value: string; label: string }[] = (() => {
  const opts: { value: string; label: string }[] = [{ value: '', label: 'All' }];
  for (let y = currentYear - 5; y <= currentYear + 5; y++) {
    opts.push({ value: String(y), label: String(y) });
  }
  return opts;
})();

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const formatCurrency = (amount?: number | null): string => {
  if (amount == null) return '-';
  return amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Column definitions for the ColumnSelector
const columnDefinitions: ColumnDefinition[] = [
  { id: 'code',               label: 'Code',        field: 'code',               required: true,  defaultVisible: true,  sortable: true },
  { id: 'engagementTypeName', label: 'Type',         field: 'engagementTypeName', required: true,  defaultVisible: true,  sortable: true },
  { id: 'clientName',         label: 'Client',       field: 'clientName',         required: true,  defaultVisible: true,  sortable: true },
  { id: 'year',               label: 'Year',         field: 'year',               required: false, defaultVisible: true,  sortable: true },
  { id: 'managerName',        label: 'Manager',      field: 'managerName',        required: false, defaultVisible: true,  sortable: true },
  { id: 'description',        label: 'Description',  field: 'description',        required: false, defaultVisible: true,  sortable: false },
  { id: 'budgetAmount',       label: 'Budget',       field: 'budgetAmount',       required: false, defaultVisible: false, sortable: true },
  { id: 'status',             label: 'Status',       field: 'statusName',         required: false, defaultVisible: true,  sortable: true },
  { id: 'startDate',          label: 'Start Date',   field: 'startDate',          required: false, defaultVisible: false, sortable: true },
  { id: 'endDate',            label: 'End Date',     field: 'endDate',            required: false, defaultVisible: false, sortable: true },
  { id: 'createdAt',          label: 'Created',      field: 'createdAt',          required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',          label: 'Updated',      field: 'updatedAt',          required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'engagement-master-columns';

interface EngagementMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const EngagementMasterScreen: React.FC<EngagementMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  // Visibility scope is enforced server-side on the engagement-management module:
  //   view-all-employees → unrestricted
  //   view-own-team      → list shows engagements where (manager_id ∈ team OR
  //                        client.partner_id ∈ team)
  //   neither            → only engagements where the caller is the manager OR
  //                        the partner of the client
  // Manager dropdown stays loose (shows all managers) — backend rejects out-of-scope
  // picks with 403. Partner dropdown is intentionally loose per product decision.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _permissions = {
    canViewAll: allowedActions.includes('view-all-employees'),
    canViewTeam: allowedActions.includes('view-own-team'),
  };

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --------------------------------------------------------------------------
  // Column visibility state
  // --------------------------------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // --------------------------------------------------------------------------
  // Lookup data
  // --------------------------------------------------------------------------
  const { data: clientsRaw, refetch: refetchClients } = useClients({
    page: 1, pageSize: 500, includeInactive: false,
  });
  const { data: engagementTypesRaw, refetch: refetchEngagementTypes } = useEngagementTypes({
    page: 1, pageSize: 500, includeInactive: false,
  });
  const { data: managersRaw, refetch: refetchManagers } = useEmployees({
    page: 1, pageSize: 500, isManager: true, includeInactive: false,
  });
  const { data: partnersRaw, refetch: refetchPartners } = useEmployees({
    page: 1, pageSize: 500, isPartner: true, includeInactive: false,
  });

  const clientOptions = useMemo(
    () => clientsRaw.map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` })),
    [clientsRaw]
  );
  const engagementTypeOptions = useMemo(
    () => engagementTypesRaw.map((et) => ({ value: et.id, label: et.name })),
    [engagementTypesRaw]
  );
  const managerOptions = useMemo(
    () => managersRaw.map((e) => ({
      value: e.id,
      label: e.isPartner
        ? `★ ${e.code} - ${e.name} (Partner)`
        : `${e.code} - ${e.name}`,
    })),
    [managersRaw]
  );
  const partnerOptions = useMemo(
    () => partnersRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` })),
    [partnersRaw]
  );

  // Map managerId -> partnerId for auto-filling partner when manager is selected
  const managerPartnerMap = useMemo(
    () => Object.fromEntries(
      managersRaw.map((e) => [
        e.id,
        e.partnerId || (e.isPartner ? e.id : ''),
      ]).filter(([, pid]) => pid !== '')
    ),
    [managersRaw]
  );

  // Filter options
  const clientFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...clientsRaw.map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }))],
    [clientsRaw]
  );
  const engagementTypeFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...engagementTypesRaw.map((et) => ({ value: et.id, label: et.name }))],
    [engagementTypesRaw]
  );
  const managerFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...managersRaw.map((e) => ({
      value: e.id,
      label: e.isPartner ? `★ ${e.code} - ${e.name}` : `${e.code} - ${e.name}`,
    }))],
    [managersRaw]
  );

  const loadLookups = useCallback(() => {
    refetchClients();
    refetchEngagementTypes();
    refetchManagers();
    refetchPartners();
  }, [refetchClients, refetchEngagementTypes, refetchManagers, refetchPartners]);

  // --------------------------------------------------------------------------
  // Server-side query state
  // --------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<EngagementSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useEngagements(queryParams);

  // --------------------------------------------------------------------------
  // View state
  // --------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [codeFilter, setCodeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [engagementTypeFilter, setEngagementTypeFilter] = useState<string>('');
  const [managerFilter, setManagerFilter] = useState<string>('');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // --------------------------------------------------------------------------
  // Modal state
  // --------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Engagement | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      loadLookups();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.code === recordCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data, loadLookups]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Engagement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mutation hooks
  const { createEngagement } = useCreateEngagement();
  const { updateEngagement } = useUpdateEngagement();
  const { deleteEngagement } = useDeleteEngagement();

  // --------------------------------------------------------------------------
  // Column definitions for SyutGrid
  // --------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<Engagement>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', accessorKey: 'code', size: 150, enableSorting: true },
      {
        id: 'engagementTypeName',
        header: 'Type',
        accessorKey: 'engagementTypeName',
        size: 150,
        enableSorting: true,
        cell: ({ row }) => row.original.engagementTypeName || '-',
      },
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            title={row.original.clientName || ''}
            style={{
              maxWidth: '180px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.clientName || '-'}
          </div>
        ),
      },
      { id: 'year', header: 'Year', accessorKey: 'year', size: 80, enableSorting: true },
      {
        id: 'managerName',
        header: 'Manager',
        accessorKey: 'managerName',
        size: 150,
        enableSorting: true,
        cell: ({ row }) => row.original.managerName || '-',
      },
      {
        id: 'description',
        header: 'Description',
        accessorKey: 'description',
        size: 250,
        enableSorting: false,
        cell: ({ row }) => (
          <div
            title={row.original.description}
            style={{
              maxWidth: '230px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.description || '-'}
          </div>
        ),
      },
      {
        id: 'budgetAmount',
        header: 'Budget',
        accessorKey: 'budgetAmount',
        size: 100,
        enableSorting: true,
        cell: ({ row }) => formatCurrency(row.original.budgetAmount),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 100,
        enableSorting: true,
        cell: ({ row }) => {
          const code = row.original.statusCode || '';
          const badge = STATUS_BADGE_MAP[code] || { className: 'badge-inactive', label: code };
          return (
            <span className={`badge ${badge.className}`}>
              {row.original.statusName || badge.label}
            </span>
          );
        },
      },
      {
        id: 'startDate',
        header: 'Start Date',
        accessorKey: 'startDate',
        size: 100,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.startDate),
      },
      {
        id: 'endDate',
        header: 'End Date',
        accessorKey: 'endDate',
        size: 100,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.endDate),
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<Engagement>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // --------------------------------------------------------------------------
  // Handlers -- open modals
  // --------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record: Engagement) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(record);
      setFormMode('edit');
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((record: Engagement) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Engagement | null>(null);

  const handleView = useCallback((record: Engagement) => {
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  // ----- Closure / Reopen / History modals -----
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<Engagement | null>(null);
  // Saved state from a minimized close session — re-hydrated on restore so the
  // user does not lose their typed-in invoiced amount / remarks etc.
  const [closeRestoredState, setCloseRestoredState] = useState<any>(null);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenTarget, setReopenTarget] = useState<Engagement | null>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<Engagement | null>(null);

  const handleCloseEngagementClick = useCallback((record: Engagement) => {
    setCloseRestoredState(null);
    setCloseTarget(record);
    setCloseModalOpen(true);
  }, []);

  const handleRestoreCloseModal = useCallback((eng: Engagement, savedState: any) => {
    setCloseRestoredState(savedState || null);
    setCloseTarget(eng);
    setCloseModalOpen(true);
  }, []);

  const handleReopenEngagementClick = useCallback((record: Engagement) => {
    setReopenTarget(record);
    setReopenModalOpen(true);
  }, []);

  const handleClosureHistoryClick = useCallback((record: Engagement) => {
    setHistoryTarget(record);
    setHistoryModalOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Save handler
  // --------------------------------------------------------------------------
  const handleSaveForm = useCallback(
    async (formData: EngagementFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        if (formMode === 'create') {
          const payload: CreateEngagementRequest = {
            code: formData.code || undefined,  // Empty = auto-generated by backend
            clientId: formData.clientId,
            engagementTypeId: formData.engagementTypeId,
            year: Number(formData.year),
            managerId: formData.managerId,
            description: formData.description,
            budgetAmount: formData.budgetAmount ? parseFloat(formData.budgetAmount) : undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            statusCode: formData.statusCode,
          };
          const created = await createEngagement(payload);
          notificationService.toast.success(`Engagement "${created?.code || payload.code || ''}" created.`);
        } else if (selectedRecord) {
          const payload: UpdateEngagementRequest = {
            code: formData.code || undefined,  // Empty = auto-generated by backend
            clientId: formData.clientId,
            engagementTypeId: formData.engagementTypeId,
            year: Number(formData.year),
            managerId: formData.managerId,
            description: formData.description,
            budgetAmount: formData.budgetAmount ? parseFloat(formData.budgetAmount) : undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            statusCode: formData.statusCode,
            rowVersion: selectedRecord.rowVersion,
          };
          await updateEngagement(selectedRecord.id, payload);
          notificationService.toast.success(`Engagement "${payload.code}" updated.`);
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
        loadLookups();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save engagement. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createEngagement, updateEngagement, refetch, loadLookups]
  );

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteEngagement(deleteTarget.id);
      notificationService.toast.success(`Engagement "${deleteTarget.code}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete engagement.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteEngagement, refetch]);

  // --------------------------------------------------------------------------
  // Filter Apply / Clear
  // --------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.clientId) count++;
    if (queryParams.engagementTypeId) count++;
    if (queryParams.managerId) count++;
    if (queryParams.year) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.code, queryParams.clientId, queryParams.engagementTypeId, queryParams.managerId, queryParams.year, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      clientId: clientFilter || undefined,
      engagementTypeId: engagementTypeFilter || undefined,
      managerId: managerFilter || undefined,
      year: yearFilter ? Number(yearFilter) : undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, clientFilter, engagementTypeFilter, managerFilter, yearFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setClientFilter('');
    setEngagementTypeFilter('');
    setManagerFilter('');
    setYearFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: Engagement): ActionMenuItem[] => {
      const isClosed = (row.statusCode || '').toUpperCase() === 'CLOSED';
      // Order (top → bottom): View, Edit, Close / Reopen, Closure History, Delete.
      // Delete sits at the bottom because it is destructive — visually separated
      // from the routine actions so it cannot be picked accidentally.
      const items: ActionMenuItem[] = [
        {
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        },
      ];
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
          disabled: isClosed,
        });
      }
      // Close — visible only on non-closed engagements when caller has 'close'.
      if (!isClosed && allowedActions.includes('close')) {
        items.push({
          id: 'close-engagement',
          label: 'Close Engagement…',
          icon: <IconSystem.lock size={16} />,
          onClick: () => handleCloseEngagementClick(row),
        });
      }
      // Reopen — visible only on closed engagements when caller has 'reopen'.
      if (isClosed && allowedActions.includes('reopen')) {
        items.push({
          id: 'reopen-engagement',
          label: 'Reopen Engagement…',
          icon: <IconSystem.refresh size={16} />,
          onClick: () => handleReopenEngagementClick(row),
        });
      }
      // Closure History — visible whenever the engagement has at least one closure.
      if ((row.closureCount ?? 0) > 0) {
        items.push({
          id: 'closure-history',
          label: 'Closure History',
          icon: <IconSystem.list size={16} />,
          onClick: () => handleClosureHistoryClick(row),
        });
      }
      // Delete is added LAST (bottom of the menu).
      // Disabled when closed — keeps the menu predictable and the read-only
      // state visible, but the entry never moves.
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
          disabled: isClosed,
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick,
     handleCloseEngagementClick, handleReopenEngagementClick, handleClosureHistoryClick],
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add New Engagement',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch]
  );

  // Form initial data (edit mode)
  const formInitialData: EngagementFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;

    return {
      code: selectedRecord.code,
      clientId: selectedRecord.clientId || '',
      engagementTypeId: selectedRecord.engagementTypeId || '',
      year: String(selectedRecord.year ?? currentYear),
      managerId: selectedRecord.managerId || '',
      partnerId: '',
      description: selectedRecord.description || '',
      budgetAmount: String(selectedRecord.budgetAmount ?? ''),
      startDate: selectedRecord.startDate ? selectedRecord.startDate.substring(0, 10) : '',
      endDate: selectedRecord.endDate ? selectedRecord.endDate.substring(0, 10) : '',
      statusCode: selectedRecord.statusCode || 'OPEN',
    };
  }, [formMode, selectedRecord]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Engagement Management"
        description="Manage client engagements from creation to closure"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.file size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, client, type...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* List view */}
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={
                  loading ? 'Loading engagements...' : 'No engagements found'
                }
              />
            )}

            {/* Grid (card) view */}
            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {loading ? 'Loading engagements...' : 'No engagements found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const itemBadge = STATUS_BADGE_MAP[item.statusCode || ''] || { className: 'badge-inactive', label: item.statusCode };
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {item.code}
                            </div>
                            <h3
                              title={item.clientName || ''}
                              style={{
                                fontSize: '1rem',
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                margin: 0,
                                marginBottom: '0.25rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                              }}
                            >
                              {item.clientName || '-'}
                            </h3>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                              {item.engagementTypeName || '-'} &middot; {item.year}
                            </div>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        <div
                          title={item.description}
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-muted)',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.4,
                          }}
                        >
                          {item.description || '-'}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 'auto', flexWrap: 'wrap' }}>
                          {item.managerName && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              {item.managerName}
                            </span>
                          )}
                          {item.budgetAmount != null && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              {formatCurrency(item.budgetAmount)}
                            </span>
                          )}
                          <span className={`badge ${itemBadge.className}`}>
                            {item.statusName || itemBadge.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input
                type="text"
                className="form-input"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Client"
                options={clientFilterOptions}
                value={clientFilter}
                onChange={(val) => setClientFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Engagement Type"
                options={engagementTypeFilterOptions}
                value={engagementTypeFilter}
                onChange={(val) => setEngagementTypeFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutScopedEmployeeSelect
                label="Manager"
                scopeMode="view"
                moduleId="engagement-management"
                role="manager"
                value={managerFilter || undefined}
                onChange={(val) => setManagerFilter(val || '')}
                isClearable
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Year"
                options={YEAR_FILTER_OPTIONS}
                value={yearFilter}
                onChange={(val) => setYearFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* Form modal */}
      <EngagementFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        clientOptions={clientOptions}
        engagementTypeOptions={engagementTypeOptions}
        managerOptions={managerOptions}
        partnerOptions={partnerOptions}
        managerPartnerMap={managerPartnerMap}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Engagement"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.code}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {/* Close Engagement Modal */}
      <EngagementCloseModal
        isOpen={closeModalOpen}
        engagement={closeTarget}
        onClose={() => { setCloseModalOpen(false); setCloseTarget(null); setCloseRestoredState(null); }}
        onClosed={() => { refetch(); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreCloseModal}
        restoredState={closeRestoredState}
      />

      {/* Reopen Engagement Modal */}
      <EngagementReopenModal
        isOpen={reopenModalOpen}
        engagement={reopenTarget}
        onClose={() => { setReopenModalOpen(false); setReopenTarget(null); }}
        onReopened={() => { refetch(); }}
      />

      {/* Closure History Modal */}
      <EngagementClosureHistoryModal
        isOpen={historyModalOpen}
        engagement={historyTarget}
        onClose={() => { setHistoryModalOpen(false); setHistoryTarget(null); }}
      />

      {/* View Modal -- read-only engagement details */}
      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-lg">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Engagement — {viewRecord.code}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.code || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Year</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.year || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Client</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.clientName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Engagement Type</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.engagementTypeName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Manager</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.managerName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Description</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.description || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Budget Amount</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatCurrency(viewRecord.budgetAmount)}</div>
                </div>
                <div>
                  <label className="form-label">Start Date</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.startDate)}</div>
                </div>
                <div>
                  <label className="form-label">End Date</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.endDate)}</div>
                </div>
                <div>
                  <label className="form-label">Total WIP</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatCurrency(viewRecord.totalWipAmount)}</div>
                </div>
                <div>
                  <label className="form-label">Total Invoiced</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatCurrency(viewRecord.totalInvoicedAmount)}</div>
                </div>
                <div>
                  <label className="form-label">Total Paid</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatCurrency(viewRecord.totalPaidAmount)}</div>
                </div>
                <div>
                  <label className="form-label">Outstanding</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatCurrency(viewRecord.totalOutstandingAmount)}</div>
                </div>
                <div>
                  <label className="form-label">Last Invoice</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.lastInvoiceNumber || '—'}{viewRecord.lastInvoiceDate ? ` (${formatDate(viewRecord.lastInvoiceDate)})` : ''}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge ${(STATUS_BADGE_MAP[viewRecord.statusCode || ''] || { className: 'badge-inactive' }).className}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
                <div>
                  <label className="form-label">Created</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.createdAt)}{viewRecord.createdByName ? ` by ${viewRecord.createdByName}` : ''}</div>
                </div>
                {viewRecord.updatedAt && (
                  <div>
                    <label className="form-label">Updated</label>
                    <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.updatedAt)}{viewRecord.updatedByName ? ` by ${viewRecord.updatedByName}` : ''}</div>
                  </div>
                )}
                {viewRecord.closedDate && (
                  <div>
                    <label className="form-label">Closed</label>
                    <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.closedDate)}{viewRecord.closureRemarks ? ` — ${viewRecord.closureRemarks}` : ''}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EngagementMasterScreen;
